/**
 * Couleurs des graphiques — UN seul endroit, validé (lisibilité, daltonisme, contraste sur
 * fond blanc) avec le validateur de palette data-viz. Un graphique ne choisit jamais sa
 * couleur lui-même : il prend son rôle ici.
 *
 * Les couleurs d'ÉTAT d'un club ne sont pas ici : elles viennent de la codification
 * `FIN_STATUT_CLUB` (voir statut-club.ts), comme partout ailleurs.
 */
export const COULEURS = {
  /** Réglé / entrées — sarcelle saturée (le `primary` de la marque est trop gris pour un tracé). */
  positif: '#009688',
  /** En cours de vérification. */
  verification: '#2a78d6',
  /** Dû / sorties. */
  negatif: '#eb6834',
  /** Résultat net (point) — encre neutre, ni entrée ni sortie. */
  net: '#292524',
  /** Hors clubs (recettes) — distinct des frais clubs. */
  horsClub: '#2a78d6',
} as const;

/**
 * Ordre FIXE des teintes catégorielles : la couleur suit l'entité (sa place dans la
 * codification), jamais son rang du moment. Au-delà de 7, le reste partage la dernière.
 */
export const CATEGORIELLES = ['#009688', '#eb6834', '#2a78d6', '#eda100', '#e87ba4', '#008300', '#4a3aa7'];

export const categorielle = (index: number): string =>
  CATEGORIELLES[Math.min(Math.max(index, 0), CATEGORIELLES.length - 1)];

/** Rampe ordinale du retard — une teinte, du plus clair (peu en retard) au plus foncé. */
const RAMPE_RETARD = ['#f29b6e', '#ec7a45', '#d85a26', '#ad4318', '#782c0c'];

/**
 * `n` marches de la rampe du retard, réparties sur toute sa longueur : avec 3 niveaux de
 * relance, le dernier reste le plus foncé au lieu de s'arrêter au milieu de la rampe.
 */
export const rampeRetard = (n: number): string[] => {
  if (n <= 0) return [];
  if (n === 1) return [RAMPE_RETARD[RAMPE_RETARD.length - 1]];
  if (n >= RAMPE_RETARD.length) {
    return Array.from({ length: n }, (_, i) => RAMPE_RETARD[Math.min(i, RAMPE_RETARD.length - 1)]);
  }
  return Array.from({ length: n }, (_, i) => RAMPE_RETARD[Math.round(i * (RAMPE_RETARD.length - 1) / (n - 1))]);
};

/** 1 234 567 → « 1,2 M » ; 12 300 → « 12 k » : graduations d'axe seulement, jamais une valeur lue. */
export const compact = (v: number): string => {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(a >= 10_000_000 ? 0 : 1).replace('.', ',')} M`;
  if (a >= 1_000) return `${(v / 1_000).toFixed(a >= 10_000 ? 0 : 1).replace('.', ',')} k`;
  return `${Math.round(v)}`;
};

/** Montant API (chaîne DECIMAL ou nombre) → nombre ; vide → 0. */
export const nb = (v: unknown): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};
